import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CheckCircle2, AlertCircle, Loader2, KeyRound } from 'lucide-react';

import { useVerifyPickup } from '../../ducks/pickup.duck';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const STATUS = Object.freeze({
  VERIFIED: 'verified',
  INVALID: 'invalid',
  LOCKED: 'locked',
  EXPIRED: 'expired',
  NOT_OWNER: 'not_owner',
  NOT_FOUND: 'not_found',
  SHARETRIBE_STATE_INVALID: 'sharetribe_state_invalid',
  SHARETRIBE_FAILED: 'verified_but_sharetribe_failed',
});

const REASON = Object.freeze({
  TX_LOOKUP_FAILED: 'tx_lookup_failed',
  TX_LOOKUP_EMPTY: 'tx_lookup_empty',
  STATE_NOT_OK_TO_VERIFY: 'state_not_ok_to_verify',
  SELLER_ACCOUNT_INVALID: 'seller_account_invalid',
  BUYER_ACCOUNT_INVALID: 'buyer_account_invalid',
});

const PICKUP_CODE_LENGTH = 6;
const PICKUP_CODE_PATTERN = /^\d{6}$/;

const T = {
  title: 'PickupVerifyCard.title',
  description: 'PickupVerifyCard.description',
  codeLabel: 'PickupVerifyCard.codeLabel',
  codePlaceholder: 'PickupVerifyCard.codePlaceholder',
  verify: 'PickupVerifyCard.verify',
  verifying: 'PickupVerifyCard.verifying',
  approved: 'PickupVerifyCard.approved',
  alreadyVerified: 'PickupVerifyCard.alreadyVerified',
  invalid: 'PickupVerifyCard.invalid',
  invalidNoAttempts: 'PickupVerifyCard.invalidNoAttempts',
  locked: 'PickupVerifyCard.locked',
  expired: 'PickupVerifyCard.expired',
  notOwner: 'PickupVerifyCard.notOwner',
  notFound: 'PickupVerifyCard.notFound',
  txLookupFailed: 'PickupVerifyCard.txLookupFailed',
  txLookupEmpty: 'PickupVerifyCard.txLookupEmpty',
  stateNotOkToVerify: 'PickupVerifyCard.stateNotOkToVerify',
  sellerAccountInvalid: 'PickupVerifyCard.sellerAccountInvalid',
  buyerAccountInvalid: 'PickupVerifyCard.buyerAccountInvalid',
  approvedSharetribeFailed: 'PickupVerifyCard.approvedSharetribeFailed',
  unknownError: 'PickupVerifyCard.unknownError',
};

/** Map an EF `sharetribe_state_invalid.reason` to a translation key. */
const reasonTranslationId = reason => {
  switch (reason) {
    case REASON.TX_LOOKUP_FAILED:
      return T.txLookupFailed;
    case REASON.TX_LOOKUP_EMPTY:
      return T.txLookupEmpty;
    case REASON.STATE_NOT_OK_TO_VERIFY:
      return T.stateNotOkToVerify;
    case REASON.SELLER_ACCOUNT_INVALID:
      return T.sellerAccountInvalid;
    case REASON.BUYER_ACCOUNT_INVALID:
      return T.buyerAccountInvalid;
    default:
      return T.unknownError;
  }
};

const SuccessBanner = ({ messageId, values }) => (
  <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
    <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
    <FormattedMessage id={messageId} values={values} />
  </div>
);

const ErrorBanner = ({ messageId, values }) => (
  <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
    <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
    <FormattedMessage id={messageId} values={values} />
  </div>
);

/**
 * Render the verification result returned by the BFF / EF as a banner.
 *
 * @param {Object} data EF status envelope (passed through by BFF)
 * @returns {JSX.Element|null}
 */
const ResultBanner = ({ data }) => {
  if (!data?.status) return null;

  switch (data.status) {
    case STATUS.VERIFIED:
      return <SuccessBanner messageId={T.approved} />;
    case STATUS.INVALID:
      return data.attempts_remaining > 0 ? (
        <ErrorBanner
          messageId={T.invalid}
          values={{ attemptsRemaining: data.attempts_remaining }}
        />
      ) : (
        <ErrorBanner messageId={T.invalidNoAttempts} />
      );
    case STATUS.LOCKED:
      return <ErrorBanner messageId={T.locked} values={{ lockedUntil: data.locked_until }} />;
    case STATUS.EXPIRED:
      return <ErrorBanner messageId={T.expired} />;
    case STATUS.NOT_OWNER:
      return <ErrorBanner messageId={T.notOwner} />;
    case STATUS.NOT_FOUND:
      return <ErrorBanner messageId={T.notFound} />;
    case STATUS.SHARETRIBE_STATE_INVALID:
      return (
        <ErrorBanner
          messageId={reasonTranslationId(data.reason)}
          values={{
            lastTransition: data.last_transition,
            state: data.detail?.state,
          }}
        />
      );
    case STATUS.SHARETRIBE_FAILED:
      return (
        <ErrorBanner messageId={T.approvedSharetribeFailed} values={{ message: data.message }} />
      );
    default:
      return <ErrorBanner messageId={T.unknownError} />;
  }
};

/**
 * Seller-facing pickup verification card. Mounted on the seller's dashboard
 * and on the TransactionPage seller view for transactions in `state/purchased`.
 * Posts the buyer-presented 6-digit code to /api/pickup/verify; on `approved`
 * the BFF transitions Sharetribe to `state/delivered` so payouts can release.
 *
 * @param {Object} props
 * @param {string} props.transactionId UUID of the Sharetribe transaction
 * @returns {JSX.Element}
 */
const PickupVerifyCard = ({ transactionId, inline = false }) => {
  const intl = useIntl();
  const [code, setCode] = useState('');
  const { verify, data, error, isLoading } = useVerifyPickup(transactionId);

  const isVerified = data?.status === STATUS.VERIFIED;
  const isCodeValid = PICKUP_CODE_PATTERN.test(code);
  const displayedCode = code.length > 3 ? `${code.slice(0, 3)}\u2003${code.slice(3)}` : code;

  const handleSubmit = e => {
    e.preventDefault();
    if (!isCodeValid || isLoading || isVerified) return;
    verify(code);
  };

  const Shell = inline ? 'div' : 'article';
  const shellClass = inline
    ? ''
    : 'rounded-lg border border-border bg-card p-4';
  return (
    <Shell className={shellClass}>
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
        <KeyRound className="h-6 w-6" />
      </span>
      {!inline ? (
        <div className="flex items-start justify-between gap-3">
          <h4 className="font-heading text-base font-bold uppercase text-foreground">
            <FormattedMessage id={isVerified ? T.alreadyVerified : T.title} />
          </h4>
          <Badge variant={isVerified ? 'statusSuccess' : 'statusInfo'}>
            <FormattedMessage
              id={
                isVerified ? 'PickupVerifyCard.statusReleasing' : 'PickupVerifyCard.statusAwaiting'
              }
            />
          </Badge>
        </div>
      ) : null}

      <p className={inline ? 'text-sm leading-6 text-foreground/80' : 'mt-2 text-sm leading-6 text-foreground/80'}>
        <FormattedMessage id={T.description} />
      </p>

      {!isVerified ? (
        <form onSubmit={handleSubmit} className="mt-3">
          <label
            htmlFor="pickupCode"
            className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground"
          >
            <FormattedMessage id={T.codeLabel} />
          </label>
          <Input
            id="pickupCode"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={PICKUP_CODE_LENGTH + 1}
            value={displayedCode}
            onChange={e =>
              setCode(e.target.value.replace(/\D/g, '').slice(0, PICKUP_CODE_LENGTH))
            }
            placeholder={intl.formatMessage({ id: T.codePlaceholder })}
            className="mt-2 h-16 text-center font-mono text-4xl font-bold tracking-[0.3em] text-foreground placeholder:text-muted-foreground/40"
          />

          <ResultBanner data={data} />

          {error && !data ? (
            <ErrorBanner
              messageId={T.unknownError}
              values={{ message: error?.data?.error || error?.error || '' }}
            />
          ) : null}

          <Button
            type="submit"
            disabled={!isCodeValid || isLoading}
            className="mt-3 h-11 w-full rounded-lg font-heading uppercase"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <FormattedMessage id={T.verifying} />
              </>
            ) : (
              <FormattedMessage id={T.verify} />
            )}
          </Button>
        </form>
      ) : (
        <div className="mt-3 rounded-lg border border-green-500/20 bg-green-500/10 p-3 text-sm text-green-800">
          <div className="flex items-center gap-2 font-semibold text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            <FormattedMessage id={T.approved} />
          </div>
          <p className="mt-1 leading-5">
            <FormattedMessage id="PickupVerifyCard.payoutQueued" />
          </p>
        </div>
      )}
    </Shell>
  );
};

export default PickupVerifyCard;
